import { useState } from 'react';
import FileDownload from 'js-file-download';
import { toast } from 'react-toastify';
import { store } from '../../../../redux/oldStore';
import { ServerResponse } from '../../../../utils/Server';
import { splitCSV, NewGuid } from '../../../../utils/Tools';
import Icon, { IconType } from '../../../Icon/Icon';
import { UploadType } from '../../../Import/ParticipantsUpload';
import {
  BulkInviteMappingOptions,
  BulkInviteMappingTarget,
  BulkInviteRoles,
  MaxBulkInviteRows,
  AutoMapHeader,
  IBulkInviteRow,
  IBulkInviteResult,
  IsRowBlank,
  IsRowValid,
  IsValidEmail,
} from './BulkInviteTypes';

interface IProps {
  onInvited: () => void;
}

type Step = 'upload' | 'map' | 'grid' | 'result';

const emptyRow = (): IBulkInviteRow => ({
  Key: NewGuid(),
  FirstName: '',
  LastName: '',
  Email: '',
  Phone: '',
  Role: 'Participant',
});

const BulkInviteForm = (props: IProps) => {
  const [step, setStep] = useState<Step>('upload');
  const [headers, setHeaders] = useState<string[]>([]);
  const [dataLines, setDataLines] = useState<string[][]>([]);
  const [mapping, setMapping] = useState<BulkInviteMappingTarget[]>([]);
  const [rows, setRows] = useState<IBulkInviteRow[]>([]);
  const [defaultRole, setDefaultRole] = useState<string>('Participant');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<IBulkInviteResult>();
  const [pasteText, setPasteText] = useState('');
  const [appendOnAdd, setAppendOnAdd] = useState(true);

  const startOver = () => {
    setStep('upload');
    setHeaders([]);
    setDataLines([]);
    setMapping([]);
    setRows([]);
    setDefaultRole('Participant');
    setResult(undefined);
    setPasteText('');
    setAppendOnAdd(true);
  };

  const downloadTemplate = () => {
    const header = ['Full Name', 'Email', 'Phone Number', 'Role'];
    const csv = [header.join(','), ['', '', '', 'Participant'].join(',')].join('\r\n');
    FileDownload(csv, 'bulk_invite_template.csv');
  };

  const parseIntoMapping = (text: string, delimiter: UploadType, emptyMessage: string) => {
    const normalized = text.replaceAll('\r\n', '\n').replaceAll('\r', '\n');
    const lines = normalized.split('\n').filter((l) => l.trim().length > 0).map((l) => splitCSV(l, delimiter));

    if (lines.length === 0) {
      toast.error(emptyMessage);
      return;
    }

    const parsedHeaders = lines[0];
    const parsedDataLines = lines.slice(1);

    setHeaders(parsedHeaders);
    setDataLines(parsedDataLines);
    setMapping(parsedHeaders.map((h) => AutoMapHeader(h)));
    setStep('map');
  };

  const onFileSelected = (e: any) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt: any) => {
      parseIntoMapping(evt.target.result as string, 'comma', 'That file did not have any rows in it.');
    };
    reader.readAsText(file, 'utf-8-sig');
  };

  const onParsePastedData = () => {
    if (!pasteText.trim()) {
      toast.error('Paste some data first.');
      return;
    }
    const delimiter: UploadType = pasteText.includes('\t') ? 'tab' : 'comma';
    parseIntoMapping(pasteText, delimiter, 'That did not have any rows in it.');
  };

  const startManualEntry = () => {
    setRows((prev) => [...prev, emptyRow()]);
    setStep('grid');
  };

  const buildRowsFromMapping = () => {
    const built: IBulkInviteRow[] = dataLines.map((line) => {
      const row = emptyRow();
      row.Role = defaultRole;

      mapping.forEach((target, colIndex) => {
        const value = (line[colIndex] ?? '').trim();
        if (!value) return;

        switch (target) {
          case 'fullName': {
            const parts = value.split(' ');
            row.FirstName = parts.shift() ?? '';
            row.LastName = parts.join(' ');
            break;
          }
          case 'firstName':
            row.FirstName = value;
            break;
          case 'lastName':
            row.LastName = value;
            break;
          case 'email':
            row.Email = value;
            break;
          case 'phone':
            row.Phone = value;
            break;
          case 'role':
            if (BulkInviteRoles.some((r) => r.toLowerCase() === value.toLowerCase())) {
              row.Role = BulkInviteRoles.find((r) => r.toLowerCase() === value.toLowerCase()) ?? defaultRole;
            }
            break;
        }
      });

      return row;
    });

    const newRows = built.filter((r) => !IsRowBlank(r));

    if (rows.length > 0 && !appendOnAdd) {
      if (!window.confirm(`This will replace your ${rows.length} existing row${rows.length === 1 ? '' : 's'} with ${newRows.length} new row${newRows.length === 1 ? '' : 's'}. Continue?`)) {
        return;
      }
      setRows(newRows);
    } else {
      setRows([...rows, ...newRows]);
    }
    setAppendOnAdd(true);
    setStep('grid');
  };

  const updateMapping = (colIndex: number, target: BulkInviteMappingTarget) => {
    const next = [...mapping];
    next[colIndex] = target;
    setMapping(next);
  };

  const updateRow = (key: string, field: keyof IBulkInviteRow, value: string) => {
    setRows(rows.map((r) => (r.Key === key ? { ...r, [field]: value } : r)));
  };

  const removeRow = (key: string) => {
    setRows(rows.filter((r) => r.Key !== key));
  };

  const addRow = () => {
    const row = emptyRow();
    row.Role = defaultRole;
    setRows([...rows, row]);
  };

  const applyDefaultRoleToAll = () => {
    setRows(rows.map((r) => ({ ...r, Role: defaultRole })));
  };

  const invalidRowCount = rows.filter((r) => !IsRowBlank(r) && !IsRowValid(r)).length;
  const submittableRows = rows.filter((r) => !IsRowBlank(r) && IsRowValid(r));

  const submit = () => {
    if (submitting || submittableRows.length === 0) return;
    if (submittableRows.length > MaxBulkInviteRows) {
      toast.error(`You can invite at most ${MaxBulkInviteRows} people at a time.`);
      return;
    }
    setSubmitting(true);

    store.server
      .postApi<ServerResponse<IBulkInviteResult>>('../Invite/BulkInvite', {
        Rows: submittableRows.map((r) => ({
          FirstName: r.FirstName,
          LastName: r.LastName,
          Email: r.Email,
          Phone: r.Phone,
          Role: r.Role,
        })),
      })
      .then((x) => {
        setSubmitting(false);
        if (x.Success) {
          setResult(x.Value);
          setStep('result');
          props.onInvited();
        } else {
          toast.error(x.Message, { autoClose: false });
        }
      })
      .catch((err) => {
        setSubmitting(false);
        toast.error(`${err}`, { autoClose: false });
      });
  };

  return (
    <div>
      {step === 'upload' && (
        <div>
          <p>
            Add people to invite by uploading a CSV file, pasting data copied from a spreadsheet, or entering rows
            manually. Each person will receive an email with a registration link they can use to complete their own
            registration. Links expire after 14 days. Up to {MaxBulkInviteRows} people per batch. Admin accounts
            cannot be invited here.
          </p>

          {rows.length > 0 && (
            <div className="bumper-l">
              <button type="button" className="btn btn-secondary" onClick={() => setStep('grid')}>
                <Icon type={IconType.back} /> Back to Preview ({rows.length} row{rows.length === 1 ? '' : 's'})
              </button>
            </div>
          )}

          <div className="flex-between">
            <h4>Upload a File</h4>
            <button type="button" className="btn btn-secondary btn-xs" onClick={downloadTemplate}>
              <Icon type={IconType.download} /> Download Template
            </button>
          </div>
          <div className="bumper-l">
            <label>CSV File (first row should be column headers):</label>
            <input type="file" accept=".csv" onChange={onFileSelected} onClick={(e: any) => (e.target.value = null)} />
          </div>

          <h4 className="bumper-l">Paste Data</h4>
          <div className="bumper-l">
            <label>Paste rows copied from Excel or a CSV (first row should be column headers):</label>
            <textarea
              className="form-control"
              rows={5}
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
            />
            <div className="bumper-l">
              <button type="button" className="btn btn-secondary" onClick={onParsePastedData}>
                Continue with Pasted Data
              </button>
            </div>
          </div>

          <h4 className="bumper-l">Enter Rows Manually</h4>
          <div className="bumper-l">
            <button type="button" className="btn btn-secondary" onClick={startManualEntry}>
              <Icon type={IconType.edit} /> Enter Rows Manually
            </button>
          </div>
        </div>
      )}

      {step === 'map' && (
        <div>
          <p>Match each column in your file to a field. Columns set to "Ignore this column" will not be imported.</p>
          <table className="table">
            <thead>
              <tr>
                <th>Column</th>
                <th>Sample Value</th>
                <th>Maps To</th>
              </tr>
            </thead>
            <tbody>
              {headers.map((h, i) => (
                <tr key={`map-${i}`}>
                  <td>{h || <em>(blank header)</em>}</td>
                  <td>{dataLines[0]?.[i]}</td>
                  <td>
                    <select
                      className="form-control"
                      value={mapping[i]}
                      onChange={(e) => updateMapping(i, e.target.value as BulkInviteMappingTarget)}
                    >
                      {BulkInviteMappingOptions.map((o) => (
                        <option key={o.key} value={o.key}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="bumper-l">
            <label>Default role for rows without a mapped role: </label>
            <select className="form-control" value={defaultRole} onChange={(e) => setDefaultRole(e.target.value)}>
              {BulkInviteRoles.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
          {rows.length > 0 && (
            <div className="bumper-l">
              <label>
                <input type="radio" checked={appendOnAdd} onChange={() => setAppendOnAdd(true)} /> Add to my existing{' '}
                {rows.length} row{rows.length === 1 ? '' : 's'}
              </label>
              <label className="bumper-l">
                <input type="radio" checked={!appendOnAdd} onChange={() => setAppendOnAdd(false)} /> Replace my
                existing rows
              </label>
            </div>
          )}
          <div className="bumper-l">
            <button type="button" className="btn btn-success" onClick={buildRowsFromMapping}>
              Continue to Preview
            </button>
          </div>
        </div>
      )}

      {step === 'grid' && (
        <div>
          <div className="flex-between">
            <div>
              {rows.length} row{rows.length === 1 ? '' : 's'}
              {invalidRowCount > 0 && (
                <span className="text-danger"> — {invalidRowCount} row{invalidRowCount === 1 ? '' : 's'} need attention</span>
              )}
            </div>
            <div>
              <span>
              <select className="form-control" value={defaultRole} onChange={(e) => setDefaultRole(e.target.value)} style={{ display: 'inline-block', width: 'auto' }}>
                {BulkInviteRoles.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
              <button type="button" className="btn btn-secondary" onClick={applyDefaultRoleToAll}>
                Set All Roles
              </button>
              <button type="button" className="btn btn-secondary" onClick={addRow}>
                <Icon type={IconType.plus} /> Add Row
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setStep('upload')}>
                <Icon type={IconType.plus} /> Add More Data
              </button>
              </span>
            </div>
          </div>
          <table className="table">
            <thead>
              <tr>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Role</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const blank = IsRowBlank(row);
                const invalid = !blank && !IsRowValid(row);
                return (
                  <tr key={row.Key} className={invalid ? 'fill-alert-1' : undefined}>
                    <td>
                      <input className="form-control" value={row.FirstName} onChange={(e) => updateRow(row.Key, 'FirstName', e.target.value)} />
                    </td>
                    <td>
                      <input className="form-control" value={row.LastName} onChange={(e) => updateRow(row.Key, 'LastName', e.target.value)} />
                    </td>
                    <td className={!blank && !IsValidEmail(row.Email) ? 'has-error' : undefined}>
                      <input
                        className="form-control"
                        value={row.Email}
                        onChange={(e) => updateRow(row.Key, 'Email', e.target.value)}
                      />
                    </td>
                    <td>
                      <input className="form-control" value={row.Phone} onChange={(e) => updateRow(row.Key, 'Phone', e.target.value)} />
                    </td>
                    <td>
                      <select className="form-control" value={row.Role} onChange={(e) => updateRow(row.Key, 'Role', e.target.value)}>
                        {BulkInviteRoles.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <span className="click text-danger" onClick={() => removeRow(row.Key)}>
                        <Icon type={IconType.remove} />
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="bumper-l">
            <button type="button" className="btn btn-success" disabled={submitting || submittableRows.length === 0} onClick={submit}>
              {submitting ? 'Sending Invites...' : `Send ${submittableRows.length} Invite${submittableRows.length === 1 ? '' : 's'}`}
            </button>
          </div>
        </div>
      )}

      {step === 'result' && result && (
        <div>
          <h4>{result.InvitedCount} invite{result.InvitedCount === 1 ? '' : 's'} sent</h4>
          {result.Rows.filter((r) => !r.Success).length > 0 && (
            <>
              <p>The following rows were not invited:</p>
              <table className="table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {result.Rows.filter((r) => !r.Success).map((r) => (
                    <tr key={r.RowIndex}>
                      <td>{r.Email}</td>
                      <td>{r.Message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
          <button type="button" className="btn btn-secondary" onClick={startOver}>
            Invite More
          </button>
        </div>
      )}
    </div>
  );
};

export default BulkInviteForm;
